import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import VerifiedIcon from "@mui/icons-material/Verified";
import HistoryIcon from "@mui/icons-material/History";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FeatureLayout from "./FeatureLayout";

// App screenshot placeholders
import photoScreenshot1 from "../../../images/features/after_photo.png";
import photoScreenshot2 from "../../../images/features/before_photo.png";

const PhotoVerification = () => {
  const theme = useTheme();

  const benefits = [
    {
      icon: <VerifiedIcon />,
      title: "Tamper-Proof Evidence",
      description: "All photos are time-stamped and geotagged. Perfect for remote verification.",
    },
    {
      icon: <HistoryIcon />,
      title: "Before & After",
      description: "Compare cleaning quality with side-by-side before/after photos.",
    },
    {
      icon: <CloudUploadIcon />,
      title: "Automatic Storage",
      description: "All photos stored securely in the cloud. Access anytime, anywhere.",
    },
    {
      icon: <LocationOnIcon />,
      title: "Geotagged Photos",
      description: "Photos include location data. Know exactly where and when they were taken.",
    },
    {
      icon: <AccessTimeIcon />,
      title: "Time-Stamped",
      description: "Every photo includes the exact timestamp. No more disputes.",
    },
    {
      icon: <CheckCircleIcon />,
      title: "Guest Proof",
      description: "Share selected photos with guests as proof of cleaning.",
    },
  ];

  const stats = [
    { value: "100%", label: "Dispute resolution rate" },
    { value: "50k+", label: "Photos uploaded/month" },
    { value: "24/7", label: "Access" },
    { value: "1 tap", label: "To upload" },
  ];

  return (
    <FeatureLayout
      title="Photo Verification"
      subtitle="See exactly when your property is ready"
      description="No more wondering if the cleaning was done right. Get time-stamped before/after photos after every cleaning. Perfect for remote hosts who can't be there in person."
      icon={<PhotoCameraIcon sx={{ fontSize: 32 }} />}
      iconGradient="linear-gradient(135deg, #f093fb, #f5576c)"
      breadcrumbs={[
        { label: "Features", link: "/features" },
        { label: "Photo Verification" },
      ]}
      benefits={benefits}
      stats={stats}
      imageRight={photoScreenshot1}
      imageLeft={photoScreenshot2}
    >
      {/* How It Works */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 4,
            textAlign: "center",
            fontSize: { xs: "1.8rem", md: "2.5rem" },
          }}
        >
          Complete Visual Peace of Mind
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h1" sx={{ fontSize: "3rem", mb: 2 }}>📸</Typography>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Cleaner Takes Photos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cleaners upload before/after photos directly in the app
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h1" sx={{ fontSize: "3rem", mb: 2 }}>🔒</Typography>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Auto-Verified
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Photos are automatically time-stamped and geotagged
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: 3,
                height: "100%",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Typography variant="h1" sx={{ fontSize: "3rem", mb: 2 }}>✅</Typography>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                You Approve
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review and approve from your phone or computer
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Badges */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.dark, 0.02)} 100%)`,
            textAlign: "center",
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Typography variant="h3" fontWeight="800" color="primary.main">100%</Typography>
              <Typography variant="caption">Dispute Resolution</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h3" fontWeight="800" color="primary.main">24hr</Typography>
              <Typography variant="caption">Photo Storage</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h3" fontWeight="800" color="primary.main">1min</Typography>
              <Typography variant="caption">Upload Time</Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </FeatureLayout>
  );
};

export default PhotoVerification;